import React from 'react';
import { TranslateFromObject } from '../../infra/services/translations/AvailableTranslations';

/**
 * Textos corridos das tooltips das fases de pagamento.
 * Ficam locais (e não em translate/BD) porque são frases longas, com
 * interpolações, específicas deste componente.
 * Os placeholders são no formato {nome} e podem receber nós React.
 */
export const PAYMENT_PHASES_TEXTS = {
  // Rótulos das linhas de cálculo
  ROW_CARRIED_FROM_PREVIOUS: {
    pt: 'Transitados da fase anterior',
    en: 'Carried over from the previous phase',
    es: 'Transferidos de la fase anterior',
    fr: 'Reportés de la phase précédente',
  },
  ROW_CARRIED_FROM_PHASE: {
    pt: 'Transitados da {phase}',
    en: 'Carried over from {phase}',
    es: 'Transferidos de la {phase}',
    fr: 'Reportés de la {phase}',
  },
  ROW_PAYMENTS_THIS_PHASE: {
    pt: 'Pagamentos desta fase',
    en: 'Payments in this phase',
    es: 'Pagos de esta fase',
    fr: 'Paiements de cette phase',
  },
  ROW_EFFECTIVE_TOTAL: {
    pt: 'Total efetivo',
    en: 'Effective total',
    es: 'Total efectivo',
    fr: 'Total effectif',
  },
  ROW_TOTAL_PAID: {
    pt: 'Total pago',
    en: 'Total paid',
    es: 'Total pagado',
    fr: 'Total payé',
  },
  ROW_OUTSTANDING_BASE: {
    pt: 'Em falta (base do cálculo)',
    en: 'Outstanding (calculation base)',
    es: 'Pendiente (base del cálculo)',
    fr: 'Restant dû (base du calcul)',
  },
  ROW_EXPECTED_FOR_PHASE: {
    pt: 'Valor previsto para esta fase',
    en: 'Expected amount for this phase',
    es: 'Importe previsto para esta fase',
    fr: 'Montant prévu pour cette phase',
  },
  ROW_VALUE_WITH_MARGIN: {
    pt: 'Valor com margem',
    en: 'Amount with margin',
    es: 'Importe con margen',
    fr: 'Montant avec marge',
  },

  // Descrições da coluna "Pago"
  PAID_DESC_FIRST: {
    pt: 'Inclui pagamentos diretos desta fase. Se o pagamento exceder o Previsto, o excesso transita automaticamente para a {next}.',
    en: 'Includes direct payments for this phase. If the payment exceeds the Expected amount, the excess is automatically carried over to {next}.',
    es: 'Incluye los pagos directos de esta fase. Si el pago supera el importe Previsto, el exceso se transfiere automáticamente a la {next}.',
    fr: "Inclut les paiements directs de cette phase. Si le paiement dépasse le montant Prévu, l'excédent est automatiquement reporté sur la {next}.",
  },
  PAID_DESC_CARRY: {
    pt: 'Inclui pagamentos diretos desta fase e eventuais valores transitados da {prev}. Se o pagamento exceder o Previsto, o excesso transita automaticamente para a {next}.',
    en: 'Includes direct payments for this phase and any amounts carried over from {prev}. If the payment exceeds the Expected amount, the excess is automatically carried over to {next}.',
    es: 'Incluye los pagos directos de esta fase y los posibles importes transferidos de la {prev}. Si el pago supera el importe Previsto, el exceso se transfiere automáticamente a la {next}.',
    fr: "Inclut les paiements directs de cette phase et les éventuels montants reportés de la {prev}. Si le paiement dépasse le montant Prévu, l'excédent est automatiquement reporté sur la {next}.",
  },
  PAID_DESC_FINAL: {
    pt: 'Inclui pagamentos diretos desta fase e eventuais valores transitados da {prev}.',
    en: 'Includes direct payments for this phase and any amounts carried over from {prev}.',
    es: 'Incluye los pagos directos de esta fase y los posibles importes transferidos de la {prev}.',
    fr: 'Inclut les paiements directs de cette phase et les éventuels montants reportés de la {prev}.',
  },

  // Descrições da coluna "Falta"
  MISSING_DESC_PHASE: {
    pt: 'Valor ainda em falta para completar esta fase.',
    en: 'Amount still outstanding to complete this phase.',
    es: 'Importe que aún falta para completar esta fase.',
    fr: 'Montant restant à payer pour compléter cette phase.',
  },
  MISSING_DESC_TOTAL: {
    pt: 'Valor ainda em falta para liquidar a totalidade do orçamento.',
    en: 'Amount still outstanding to settle the full budget.',
    es: 'Importe que aún falta para liquidar la totalidad del presupuesto.',
    fr: 'Montant restant à payer pour solder la totalité du budget.',
  },

  // Descrições da coluna "Previsto"
  // Fases 3 e 4: os noivos veem apenas o valor já com a margem incluída, nunca as percentagens.
  EXPECTED_DESC_NO_PCT: {
    pt: 'Valor previsto para esta fase, calculado a partir do valor total ainda em falta antes desta fase. Já inclui uma margem de segurança para cobrir eventuais alterações de última hora.',
    en: 'Expected amount for this phase, based on the total amount still outstanding before this phase. It already includes a safety margin to cover any last-minute changes.',
    es: 'Importe previsto para esta fase, calculado a partir del importe total aún pendiente antes de esta fase. Ya incluye un margen de seguridad para cubrir posibles cambios de última hora.',
    fr: "Montant prévu pour cette phase, calculé à partir du montant total encore dû avant cette phase. Il inclut déjà une marge de sécurité pour couvrir d'éventuels changements de dernière minute.",
  },
  EXPECTED_DESC_FINAL: {
    pt: 'Fase final de pagamento. Corresponde à totalidade do valor ainda em falta após a {prev}.',
    en: 'Final payment phase. Corresponds to the entire amount still outstanding after {prev}.',
    es: 'Fase final de pago. Corresponde a la totalidad del importe aún pendiente después de la {prev}.',
    fr: 'Phase finale de paiement. Correspond à la totalité du montant encore dû après la {prev}.',
  },
  EXPECTED_DESC_FINAL_AFTER_3_4: {
    pt: 'Corresponde a 100% do valor total em falta após as Fases 3 e 4. É o valor de liquidação final do orçamento.',
    en: 'Corresponds to 100% of the total amount outstanding after Phases 3 and 4. This is the final settlement amount of the budget.',
    es: 'Corresponde al 100% del importe total pendiente después de las Fases 3 y 4. Es el importe de liquidación final del presupuesto.',
    fr: "Correspond à 100 % du montant total restant dû après les Phases 3 et 4. Il s'agit du montant de solde final du budget.",
  },

  // Notas de rodapé das tooltips
  FOOTNOTE_COLOR_RULES: {
    pt: 'Fica laranja se faltam ≤15 dias · Fica vermelho se a data já passou',
    en: 'Turns orange if ≤15 days remain · Turns red if the date has passed',
    es: 'Se pone naranja si faltan ≤15 días · Se pone rojo si la fecha ya pasó',
    fr: "Devient orange s'il reste ≤15 jours · Devient rouge si la date est dépassée",
  },
  FOOTNOTE_COLOR_RULES_EXCESS: {
    pt: 'Fica laranja se faltam ≤15 dias · Fica vermelho se a data já passou · Negativo a verde = excesso pago',
    en: 'Turns orange if ≤15 days remain · Turns red if the date has passed · Negative in green = overpayment',
    es: 'Se pone naranja si faltan ≤15 días · Se pone rojo si la fecha ya pasó · Negativo en verde = exceso pagado',
    fr: "Devient orange s'il reste ≤15 jours · Devient rouge si la date est dépassée · Négatif en vert = trop-payé",
  },
  FOOTNOTE_MISSING_RULES: {
    pt: 'Só é mostrado se positivo · Fica laranja se faltam ≤15 dias · Fica vermelho se a data já passou · A fase considera-se completa quando o valor em falta é ≤ 50 €',
    en: 'Only shown if positive · Turns orange if ≤15 days remain · Turns red if the date has passed · The phase is considered complete when the outstanding amount is ≤ €50',
    es: 'Solo se muestra si es positivo · Se pone naranja si faltan ≤15 días · Se pone rojo si la fecha ya pasó · La fase se considera completa cuando el importe pendiente es ≤ 50 €',
    fr: "Affiché uniquement s'il est positif · Devient orange s'il reste ≤15 jours · Devient rouge si la date est dépassée · La phase est considérée comme complète lorsque le montant restant dû est ≤ 50 €",
  },
  FOOTNOTE_FIXED_VALUE_OVERRIDE: {
    pt: 'Se existir um valor fixo configurado manualmente para a {phase}, esse valor prevalece sobre o calculado. A fase considera-se completa quando o valor em falta é ≤ 50 €.',
    en: 'If a fixed amount has been manually configured for {phase}, that amount takes precedence over the calculated one. The phase is considered complete when the outstanding amount is ≤ €50.',
    es: 'Si existe un importe fijo configurado manualmente para la {phase}, ese importe prevalece sobre el calculado. La fase se considera completa cuando el importe pendiente es ≤ 50 €.',
    fr: 'Si un montant fixe a été configuré manuellement pour la {phase}, ce montant prévaut sur le montant calculé. La phase est considérée comme complète lorsque le montant restant dû est ≤ 50 €.',
  },
  FOOTNOTE_ADJUST_PHASE_COMPLETE: {
    pt: 'O valor deverá ser ajustado descontando o que já foi pago na {phase}. A fase considera-se completa quando o valor em falta é ≤ 50 €.',
    en: 'The amount should be adjusted by deducting what has already been paid in {phase}. The phase is considered complete when the outstanding amount is ≤ €50.',
    es: 'El importe deberá ajustarse descontando lo que ya se ha pagado en la {phase}. La fase se considera completa cuando el importe pendiente es ≤ 50 €.',
    fr: 'Le montant doit être ajusté en déduisant ce qui a déjà été payé lors de la {phase}. La phase est considérée comme complète lorsque le montant restant dû est ≤ 50 €.',
  },
  FOOTNOTE_ADJUST_PHASE: {
    pt: 'O valor deverá ser ajustado descontando o que já foi pago na {phase}.',
    en: 'The amount should be adjusted by deducting what has already been paid in {phase}.',
    es: 'El importe deberá ajustarse descontando lo que ya se ha pagado en la {phase}.',
    fr: 'Le montant doit être ajusté en déduisant ce qui a déjà été payé lors de la {phase}.',
  },
  FOOTNOTE_ADJUST_PHASES_3_4: {
    pt: 'O valor deverá ser ajustado descontando os valores já pagos nas Fases 3 e 4.',
    en: 'The amount should be adjusted by deducting the amounts already paid in Phases 3 and 4.',
    es: 'El importe deberá ajustarse descontando los importes ya pagados en las Fases 3 y 4.',
    fr: 'Le montant doit être ajusté en déduisant les montants déjà payés lors des Phases 3 et 4.',
  },

  // Disclaimers apresentados abaixo da grelha / na coluna "Previsto"
  DISCLAIMER_FIXED_YEAR_VALUE: {
    pt: 'Valor fixo definido para o ano do vosso casamento.',
    en: 'Fixed amount set for the year of your wedding.',
    es: 'Importe fijo establecido para el año de vuestra boda.',
    fr: "Montant fixe défini pour l'année de votre mariage.",
  },
  DISCLAIMER_ADJUST_PHASE: {
    pt: 'O valor previsto para esta fase deverá ser ajustado, descontando o montante já pago na {phase}.',
    en: 'The expected amount for this phase should be adjusted by deducting the amount already paid in {phase}.',
    es: 'El importe previsto para esta fase deberá ajustarse descontando el importe ya pagado en la {phase}.',
    fr: 'Le montant prévu pour cette phase doit être ajusté en déduisant le montant déjà payé lors de la {phase}.',
  },
  DISCLAIMER_ADJUST_PHASES_3_4: {
    pt: 'O valor previsto deverá ser ajustado, descontando os valores já pagos nas Fases 3 e 4.',
    en: 'The expected amount should be adjusted by deducting the amounts already paid in Phases 3 and 4.',
    es: 'El importe previsto deberá ajustarse descontando los importes ya pagados en las Fases 3 y 4.',
    fr: 'Le montant prévu doit être ajusté en déduisant les montants déjà payés lors des Phases 3 et 4.',
  },
};

const PLACEHOLDER_REGEX = /(\{[a-zA-Z0-9_]+\})/g;

/**
 * Devolve a função de tradução local para o idioma ativo.
 * Substitui os placeholders {nome} pelos valores recebidos: devolve uma string
 * quando todos os valores são primitivos e um fragmento React quando algum
 * valor é um nó (permite manter, por exemplo, percentagens a negrito).
 */
export const getPhaseText = (activeLanguage) => (key, params) => {
  const template = TranslateFromObject(PAYMENT_PHASES_TEXTS[key], activeLanguage?.code || 'pt');
  if (!params || typeof template !== 'string') return template;

  const resolved = template.split(PLACEHOLDER_REGEX).map(part => {
    const match = part.match(/^\{([a-zA-Z0-9_]+)\}$/);
    if (!match) return part;
    return params[match[1]] !== undefined ? params[match[1]] : part;
  });

  if (!Object.keys(params).some(name => React.isValidElement(params[name]))) return resolved.join('');

  return (
    <React.Fragment>
      {resolved.map((part, index) => <React.Fragment key={index}>{part}</React.Fragment>)}
    </React.Fragment>
  );
};
